import { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { DayPicker } from 'react-day-picker'
import { cn } from '@/lib/utils'
import { buttonVariants } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { format, parse, isValid, isWithinInterval, isSameMonth, isBefore, startOfDay } from 'date-fns'
import PropTypes from 'prop-types'
import { Icons } from '@/theme/Images'

const VALID_START_DATE = new Date('1900-01-01')
const VALID_END_DATE = new Date('2090-12-31')

function generateWeekdayLabels() {
  return Array.from({ length: 7 }).map((_, index) => {
    const dayName = new Date(2024, 0, index + 1).toLocaleString('en-us', { weekday: 'long' })
    if (dayName === 'Saturday') {
      return dayName.substring(0, 3)
    }
    return dayName.substring(0, 2)
  })
}

const fullDatePattern = /^\d{2}\/\d{2}\/\d{4}$/

// Head component
function DatePickerHead({ inputValue, handleInputChange, onClear }) {
  const [tempDate, setTempDate] = useState(inputValue ? format(new Date(inputValue), 'MM/dd/yyyy') : '')

  const handleTempDateChange = e => {
    const newDate = e.target.value
    setTempDate(newDate)

    if (fullDatePattern.test(newDate)) {
      const parsedDate = parse(newDate, 'MM/dd/yyyy', new Date())
      if (isValid(parsedDate)) {
        handleInputChange(parsedDate)
      }
    } else if (newDate === '') {
      // Clear the date if input is empty
      onClear()
    }
  }

  const weekdays = generateWeekdayLabels()
  return (
    <thead>
      <tr className="flex gap-1 relative h-16">
        <td className="w-full">
          <div className="flex gap-2">
            <Input
              type="text"
              className="accent-[#D0D5DD] shadow-sm border-[1px] rounded-md font-[#101828] text-4 flex-1"
              value={tempDate}
              onChange={handleTempDateChange}
              autoFocus
              placeholder="mm/dd/yyyy"
            />
            {inputValue && (
              <button
                type="button"
                onClick={onClear}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md border border-gray-300"
              >
                Clear
              </button>
            )}
          </div>
        </td>
      </tr>
      <tr className="flex justify-between text-center text-sm font-medium">
        {weekdays.map(day => (
          <th key={day} className="flex-1 text-center py-2 text-[#344054] text-sm font-medium">
            {day}
          </th>
        ))}
      </tr>
    </thead>
  )
}

DatePickerHead.propTypes = {
  date: PropTypes.instanceOf(Date),
  inputValue: PropTypes.string.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  handleClickToday: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
}

const IconLeft = props => <ChevronLeft className="h-4 w-4" {...props} />
const IconRight = props => <ChevronRight className="h-4 w-4" {...props} />

function SingleDatePicker({
  className,
  classNames,
  showOutsideDays = true,
  disablePast = false,
  selected,
  onSelect,
  onClose,
  ...props
}) {
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState(selected || null)
  const [inputValue, setInputValue] = useState(selected ? format(selected, 'MM/dd/yyyy') : '')

  const handleClear = () => {
    setSelectedDate(null)
    setInputValue('')
    onSelect(null)
    onClose()
  }

  useEffect(() => {
    if (!inputValue) {
      setSelectedDate(null)
      onSelect(null)
      return
    }

    const parsedDate = parse(inputValue, 'MM/dd/yyyy', new Date())
    if (isValid(parsedDate) && isWithinInterval(parsedDate, { start: VALID_START_DATE, end: VALID_END_DATE })) {
      if (!disablePast || !isBefore(parsedDate, startOfDay(new Date()))) {
        setCurrentMonth(parsedDate)
        setSelectedDate(parsedDate)
        onSelect(parsedDate)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue])

  const handleInputChange = dateObj => {
    setInputValue(dateObj ? format(dateObj, 'MM/dd/yyyy') : '')
  }

  const handleDayClick = date => {
    if (isWithinInterval(date, { start: VALID_START_DATE, end: VALID_END_DATE }) && isSameMonth(date, currentMonth)) {
      const formatted = format(date, 'MM/dd/yyyy')
      setInputValue(formatted)
      setSelectedDate(date)
      onSelect(date)
      onClose()
    }
  }

  const modifiers = {
    disabled: [{ outside: 'month' }],
  }

  return (
    <div>
      <DayPicker
        id="date-picker"
        showOutsideDays={showOutsideDays}
        onDayClick={handleDayClick}
        selected={selectedDate}
        className={cn('p-5', className)}
        onMonthChange={setCurrentMonth}
        month={currentMonth}
        fixedWeeks
        modifiers={modifiers}
        weekStartsOn={1}
        classNames={{
          months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
          month: 'space-y-4',
          caption: 'flex justify-center pt-1 relative items-center',
          caption_label: 'text-xs font-medium',
          nav: 'space-x-1 flex items-center',
          nav_button_previous: 'absolute left-1',
          nav_button_next: 'absolute right-1',
          table: 'w-full border-collapse space-y-1',
          head_row: 'flex',
          head_cell: 'text-muted-foreground rounded-md w-10 font-normal text-[0.8rem]',
          row: 'flex w-full mt-2',
          cell: 'h-10 w-10 text-center text-xs p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
          day: cn(
            buttonVariants({ variant: 'ghost' }),
            'h-9 w-9 p-0 font-normal text-[#344054] text-sm aria-selected:opacity-100 focus:rounded-full rounded-full hover:before:bg-brand-600 hover:before:absolute hover:before:rounded-full hover:before:w-1.5 hover:before:h-1.5 hover:before:content-[""] hover:before:bottom-0 hover:before:left-1/2 hover:before:-translate-x-1/2 hover:before:mb-1'
          ),
          day_range_end: 'day-range-end',
          day_selected: 'bg-brand-600 text-white focus:bg-[#5A8090] focus:text-white rounded-full',
          day_today: 'bg-accent text-accent-foreground',
          day_outside:
            'day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30 text-[#667085] text-sm font-normal',
          day_disabled: 'text-muted-foreground opacity-50',
          day_range_middle: 'aria-selected:bg-accent aria-selected:text-accent-foreground',
          day_hidden: 'invisible',
          ...classNames,
        }}
        img={Icons.calendarRulesFormIcon}
        components={{
          Head: props => (
            <DatePickerHead
              {...props}
              inputValue={inputValue}
              handleInputChange={handleInputChange}
              onClear={handleClear}
            />
          ),
          IconLeft,
          IconRight,
          CaptionLabel: props => (
            <span className="font-semibold text-md text-dGray-700" {...props}>
              {currentMonth ? format(currentMonth, 'MMMM yyyy') : ''}
            </span>
          ),
        }}
        {...props}
      />
    </div>
  )
}

SingleDatePicker.displayName = 'SingleDatePicker'

SingleDatePicker.propTypes = {
  className: PropTypes.string,
  classNames: PropTypes.object,
  showOutsideDays: PropTypes.bool,
  disablePast: PropTypes.bool,
  disableFuture: PropTypes.bool,
  selected: PropTypes.instanceOf(Date),
  onSelect: PropTypes.func,
  onClose: PropTypes.func.isRequired,
}

export { SingleDatePicker, DatePickerHead, IconLeft, IconRight }
